import { useEffect, useRef, useState } from 'react';
import { useLocalParticipant, useRemoteParticipants, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import TranscriptLines from './TranscriptLines';
import './IntroScreen.css';

export default function IntroScreen({ transcript = [] }) {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const agentParticipant = remoteParticipants.find(
    (p) => p.kind === 4 || p.identity?.includes('agent')
  );

  const tracks = useTracks([Track.Source.Camera], { onlySubscribed: true });
  const agentVideoTrack = tracks.find(
    (t) =>
      t.source === Track.Source.Camera &&
      t.participant &&
      (t.participant.identity === agentParticipant?.identity ||
        t.participant.kind === 4 ||
        t.participant.identity?.includes('agent') ||
        t.participant.identity?.includes('avatar'))
  );

  const agentVideoRef = useRef(null);
  const userVideoRef = useRef(null);
  const attachedTrackSid = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasAgentVideo, setHasAgentVideo] = useState(false);

  useEffect(() => {
    const el = agentVideoRef.current;
    const track = agentVideoTrack?.publication?.track;
    if (!el || !track) { setHasAgentVideo(false); return; }
    if (attachedTrackSid.current === track.sid) return;
    track.attach(el);
    attachedTrackSid.current = track.sid;
    setHasAgentVideo(true);
    return () => {
      track.detach(el);
      attachedTrackSid.current = null;
      setHasAgentVideo(false);
    };
  }, [agentVideoTrack?.publication?.track?.sid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = userVideoRef.current;
    if (!el || !localParticipant) return;
    const attachCam = () => {
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      if (pub?.track) pub.track.attach(el);
    };
    attachCam();
    localParticipant.on('trackPublished', attachCam);
    localParticipant.on('localTrackPublished', attachCam);
    return () => {
      localParticipant.off('trackPublished', attachCam);
      localParticipant.off('localTrackPublished', attachCam);
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      if (pub?.track) pub.track.detach(el);
    };
  }, [localParticipant]);

  useEffect(() => {
    if (!agentParticipant) return;
    const handler = (speaking) => setIsSpeaking(speaking);
    agentParticipant.on('isSpeakingChanged', handler);
    return () => agentParticipant.off('isSpeakingChanged', handler);
  }, [agentParticipant]);

  return (
    <div className="intro-screen">
      {/* Main content row */}
      <div className="intro-content">
        {/* Avatar column */}
        <div className="intro-avatar-col">
          <div className={`intro-avatar-rings ${isSpeaking ? 'speaking' : ''}`}>
            <div className="intro-ring intro-ring-1" />
            <div className="intro-ring intro-ring-2" />
            <div className="intro-ring intro-ring-3" />
            <div className="intro-avatar-frame">
              <div className="intro-avatar-inner">
                <video
                  ref={agentVideoRef}
                  autoPlay
                  playsInline
                  className="intro-avatar-video"
                  style={{ display: hasAgentVideo ? 'block' : 'none' }}
                />
                {!hasAgentVideo && (
                  <div className="intro-avatar-placeholder">
                    <svg width="56" height="56" viewBox="0 0 80 80" fill="none">
                      <circle cx="40" cy="30" r="16" stroke="#2c8cc9" strokeWidth="2.5" fill="none" />
                      <path d="M12 68c0-15.464 12.536-28 28-28s28 12.536 28 28" stroke="#2c8cc9" strokeWidth="2.5" fill="none" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="intro-name-badge">
            <div className="intro-audio-bars">
              <span className={`intro-ab ${isSpeaking ? 'active' : ''}`} />
              <span className={`intro-ab ${isSpeaking ? 'active' : ''}`} />
              <span className={`intro-ab ${isSpeaking ? 'active' : ''}`} />
              <span className={`intro-ab ${isSpeaking ? 'active' : ''}`} />
            </div>
          </div>
        </div>

        {/* Live transcript of what the agent is saying */}
        <div className="intro-transcript">
          <TranscriptLines transcript={transcript} emptyMessage="Starting session…" />
        </div>
      </div>

      {/* PiP self-view */}
      <div className="intro-pip">
        <div className="intro-pip-header">
          <div className="intro-pip-dots">
            <span /><span /><span />
          </div>
          <span className="intro-pip-label">You</span>
          <span className="intro-pip-chevron">∧</span>
        </div>
        <video ref={userVideoRef} autoPlay playsInline muted className="intro-pip-video" />
      </div>
    </div>
  );
}
